import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Scan, Crown, Zap, LogOut, CreditCard, CheckCircle, User, Key, Copy,
  RefreshCw, Upload, Loader2, X, FileImage, Lock, Cloud, FileText,
  Layers, Code, Star, Building2, ArrowRight, Edit3, Save, Eye, EyeOff,
  Shield, Trash2, AlertTriangle, Bell, ChevronRight, BarChart2,
  Calendar, Clock, Settings, Package, ExternalLink, Check, Info, FileVideo,
  Users, UserPlus, UserMinus, Mail
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ─── Toast ─────────────────────────────────────────────────────────────────────
function Toast({ toasts }) {
  return (
    <div className="fixed top-5 right-5 z-[100] flex flex-col gap-2 pointer-events-none">
      <AnimatePresence>
        {toasts.map(t => (
          <motion.div key={t.id}
            initial={{ opacity: 0, x: 60, scale: 0.9 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 60, scale: 0.9 }}
            transition={{ type: 'spring', stiffness: 400, damping: 30 }}
            className={`flex items-center gap-3 px-4 py-3 rounded-xl border shadow-2xl font-mono text-sm pointer-events-auto ${
              t.type === 'error'
                ? 'bg-red-950 border-red-500/40 text-red-300'
                : t.type === 'warn'
                ? 'bg-amber-950 border-amber-500/40 text-amber-300'
                : 'bg-emerald-950 border-emerald-500/40 text-emerald-300'
            }`}>
            {t.type === 'error' ? <AlertTriangle className="w-4 h-4 flex-shrink-0" /> :
             t.type === 'warn'  ? <Info className="w-4 h-4 flex-shrink-0" /> :
             <CheckCircle className="w-4 h-4 flex-shrink-0" />}
            {t.msg}
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}

// ─── Section wrapper ────────────────────────────────────────────────────────────
function Section({ title, icon: Icon, accent = 'blue', children, delay = 0 }) {
  const colors = {
    blue:   'text-blue-400 bg-blue-500/10 border-blue-500/20',
    yellow: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/20',
    purple: 'text-purple-400 bg-purple-500/10 border-purple-500/20',
    red:    'text-red-400 bg-red-500/10 border-red-500/20',
    emerald:'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
    zinc:   'text-zinc-400 bg-zinc-800 border-zinc-700',
  };
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden">
      <div className="flex items-center gap-3 px-6 py-4 border-b border-zinc-800">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center border ${colors[accent]}`}>
          <Icon className="w-4 h-4" />
        </div>
        <h2 className="font-heading font-bold text-white text-base">{title}</h2>
      </div>
      <div className="p-6">{children}</div>
    </motion.div>
  );
}

// ─── Stat card ──────────────────────────────────────────────────────────────────
function StatCard({ label, value, sub, color = 'blue' }) {
  const colors = {
    blue:   'from-blue-500/10 to-blue-500/5 border-blue-500/20 text-blue-400',
    emerald:'from-emerald-500/10 to-emerald-500/5 border-emerald-500/20 text-emerald-400',
    purple: 'from-purple-500/10 to-purple-500/5 border-purple-500/20 text-purple-400',
    amber:  'from-amber-500/10 to-amber-500/5 border-amber-500/20 text-amber-400',
  };
  return (
    <div className={`bg-gradient-to-br ${colors[color]} border rounded-xl p-4`}>
      <p className="text-zinc-500 font-mono text-xs uppercase tracking-wider mb-1">{label}</p>
      <p className={`font-heading text-2xl font-black ${colors[color].split(' ').at(-1)}`}>{value}</p>
      {sub && <p className="text-zinc-600 font-mono text-xs mt-1">{sub}</p>}
    </div>
  );
}

// ─── Input field ────────────────────────────────────────────────────────────────
function Field({ label, value, onChange, type = 'text', placeholder, hint, disabled, rightEl }) {
  const [show, setShow] = useState(false);
  const isPass = type === 'password';
  return (
    <div className="space-y-1.5">
      <label className="font-mono text-xs text-zinc-500 uppercase tracking-wider">{label}</label>
      <div className="relative">
        <input
          type={isPass && !show ? 'password' : 'text'}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          className="w-full bg-zinc-800 border border-zinc-700 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl px-4 py-2.5 text-zinc-200 placeholder-zinc-600 font-mono text-sm outline-none transition-colors pr-10"
        />
        {isPass && (
          <button type="button" onClick={() => setShow(s => !s)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-600 hover:text-zinc-300 transition-colors">
            {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        )}
        {rightEl && <div className="absolute right-3 top-1/2 -translate-y-1/2">{rightEl}</div>}
      </div>
      {hint && <p className="text-zinc-600 font-mono text-xs">{hint}</p>}
    </div>
  );
}

// ─── Session Info ─────────────────────────────────────────────────────────────
function parseUserAgent(ua) {
  if (!ua) return { browser: 'Desconocido', os: 'Desconocido', device: 'desktop' };
  let browser = 'Desconocido';
  let os = 'Desconocido';
  let device = 'desktop';

  // Browser
  if (ua.includes('Edg/'))        browser = 'Microsoft Edge';
  else if (ua.includes('OPR/') || ua.includes('Opera')) browser = 'Opera';
  else if (ua.includes('Chrome')) browser = 'Google Chrome';
  else if (ua.includes('Firefox')) browser = 'Mozilla Firefox';
  else if (ua.includes('Safari') && !ua.includes('Chrome')) browser = 'Safari';

  // OS
  if (ua.includes('Windows NT 10')) os = 'Windows 10/11';
  else if (ua.includes('Windows NT 6.3')) os = 'Windows 8.1';
  else if (ua.includes('Windows'))  os = 'Windows';
  else if (ua.includes('Mac OS X')) os = 'macOS';
  else if (ua.includes('Linux'))    os = 'Linux';
  else if (ua.includes('Android'))  os = 'Android';
  else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';

  // Device
  if (ua.includes('Mobile') || ua.includes('iPhone') || ua.includes('Android')) device = 'mobile';
  else if (ua.includes('iPad') || ua.includes('Tablet')) device = 'tablet';

  return { browser, os, device };
}

function DeviceIcon({ device }) {
  if (device === 'mobile') return <span className="text-lg">📱</span>;
  if (device === 'tablet') return <span className="text-lg">📟</span>;
  return <span className="text-lg">💻</span>;
}

function SessionInfo() {
  const [session, setSession] = useState(null);
  const [geo, setGeo]         = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('veritas_token');
        // 1. Get IP + UA from backend
        const res = await axios.get(`${API_URL}/api/auth/session-info`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSession(res.data);

        // 2. Geolocate the IP (free, no key needed)
        const ip = res.data.ip;
        if (ip && ip !== '127.0.0.1' && ip !== '::1') {
          const geoRes = await fetch(`https://ip-api.com/json/${ip}?fields=status,country,regionName,city,isp,lat,lon`);
          const geoData = await geoRes.json();
          if (geoData.status === 'success') setGeo(geoData);
        }
      } catch {}
      finally { setLoading(false); }
    };
    load();
  }, []);

  const ua = session ? parseUserAgent(session.user_agent) : null;

  return (
    <Section title="Sesión activa" icon={Eye} accent="zinc">
      {loading ? (
        <div className="flex items-center gap-3 p-4">
          <Loader2 className="w-4 h-4 text-zinc-500 animate-spin" />
          <span className="text-zinc-500 font-mono text-xs">Cargando información de sesión...</span>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Main session card */}
          <div className="flex items-start gap-4 p-4 bg-zinc-800/50 border border-zinc-700/50 rounded-xl">
            <div className="flex-shrink-0 mt-0.5">
              {ua && <DeviceIcon device={ua.device} />}
            </div>
            <div className="flex-1 min-w-0 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-white font-mono text-sm font-bold">Sesión actual</p>
                <span className="flex items-center gap-1.5 text-emerald-400 font-mono text-xs">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse inline-block" />
                  Activa ahora
                </span>
              </div>

              {/* Grid of details */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5">

                {/* IP */}
                <div className="flex items-start gap-2">
                  <span className="text-zinc-600 font-mono text-xs w-16 flex-shrink-0 pt-0.5">IP</span>
                  <span className="text-zinc-200 font-mono text-xs font-bold tracking-wider">
                    {session?.ip || '—'}
                  </span>
                </div>

                {/* Location */}
                <div className="flex items-start gap-2">
                  <span className="text-zinc-600 font-mono text-xs w-16 flex-shrink-0 pt-0.5">Ubicación</span>
                  <span className="text-zinc-200 font-mono text-xs">
                    {geo
                      ? `${geo.city}, ${geo.regionName}, ${geo.country}`
                      : session?.ip === '127.0.0.1' || session?.ip === '::1'
                        ? 'Localhost (desarrollo)'
                        : 'No disponible'}
                  </span>
                </div>

                {/* ISP */}
                {geo?.isp && (
                  <div className="flex items-start gap-2">
                    <span className="text-zinc-600 font-mono text-xs w-16 flex-shrink-0 pt-0.5">Proveedor</span>
                    <span className="text-zinc-200 font-mono text-xs truncate max-w-[180px]" title={geo.isp}>{geo.isp}</span>
                  </div>
                )}

                {/* Browser */}
                {ua && (
                  <div className="flex items-start gap-2">
                    <span className="text-zinc-600 font-mono text-xs w-16 flex-shrink-0 pt-0.5">Navegador</span>
                    <span className="text-zinc-200 font-mono text-xs">{ua.browser}</span>
                  </div>
                )}

                {/* OS */}
                {ua && (
                  <div className="flex items-start gap-2">
                    <span className="text-zinc-600 font-mono text-xs w-16 flex-shrink-0 pt-0.5">Sistema</span>
                    <span className="text-zinc-200 font-mono text-xs">{ua.os}</span>
                  </div>
                )}

                {/* Dispositivo */}
                {ua && (
                  <div className="flex items-start gap-2">
                    <span className="text-zinc-600 font-mono text-xs w-16 flex-shrink-0 pt-0.5">Dispositivo</span>
                    <span className="text-zinc-200 font-mono text-xs capitalize">{ua.device === 'mobile' ? 'Móvil' : ua.device === 'tablet' ? 'Tablet' : 'Ordenador'}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Warning hint */}
          <div className="flex items-start gap-2.5 p-3 bg-amber-500/5 border border-amber-500/15 rounded-lg">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-500/70 flex-shrink-0 mt-0.5" />
            <p className="text-zinc-500 font-mono text-xs leading-relaxed">
              Si no reconoces esta IP o ubicación, cierra sesión inmediatamente y cambia tu contraseña.
            </p>
          </div>

          <p className="text-zinc-700 font-mono text-xs">
            La IP puede variar si usas VPN o red móvil. Los datos de geolocalización son aproximados.
          </p>
        </div>
      )}
    </Section>
  );
}

// ─── Main ───────────────────────────────────────────────────────────────────────
export default function UserProfile({ user: initialUser, usage, onBack, onLogout, onUpgrade, onNavigateApiDocs }) {
  const [user, setUser]           = useState(initialUser);
  const [activeTab, setActiveTab] = useState('overview');
  const [toasts, setToasts]       = useState([]);

  // Profile edit
  const [editName, setEditName]       = useState(initialUser?.name || '');
  const [savingProfile, setSaving]    = useState(false);

  // Password
  const [curPass, setCurPass]   = useState('');
  const [newPass, setNewPass]   = useState('');
  const [confPass, setConfPass] = useState('');
  const [savingPass, setSavingPass] = useState(false);

  // API Key
  const [apiKey, setApiKey]           = useState(null);
  const [loadingApiKey, setLoadingKey] = useState(false);
  const [copiedKey, setCopiedKey]     = useState(false);
  const [showKey, setShowKey]         = useState(false);

  // Plan
  const [loadingPortal, setLoadingPortal]     = useState(false);
  const [loadingCheckout, setLoadingCheckout] = useState(false);
  const [syncingPlan, setSyncingPlan]         = useState(false);

  // Batch
  const [batchFiles, setBatchFiles]   = useState([]);
  const [batchResults, setBatchResults] = useState([]);
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchError, setBatchError]   = useState(null);
  const batchRef = useRef(null);

  // Delete account
  const [deleteOpen, setDeleteOpen]     = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deletingAcc, setDeletingAcc]   = useState(false);

  // Stats
  const [stats, setStats] = useState(null);

  // Team
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamSeatsUsed, setTeamSeatsUsed] = useState(0);
  const [teamSeatsTotal, setTeamSeatsTotal] = useState(5);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [removingEmail, setRemovingEmail] = useState(null);
  const [teamLoading, setTeamLoading] = useState(false);
  // Confirm remove member popup
  const [confirmRemoveEmail, setConfirmRemoveEmail] = useState(null);
  // Leave team (for members who are not owners)
  const [leavingTeam, setLeavingTeam] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState(false);
  // Email preferences
  const [emailPrefs, setEmailPrefs] = useState({ marketing: true, blog: true });
  const [savingEmailPrefs, setSavingEmailPrefs] = useState(false);
  const [loadingEmailPrefs, setLoadingEmailPrefs] = useState(false);

  const isPro      = user?.plan === 'pro' || user?.plan === 'business';
  const isBusiness = user?.plan === 'business';
  const isGoogle   = user?.auth_provider === 'google';

  // ── toast helper ──
  let toastId = useRef(0);
  const toast = (msg, type = 'success') => {
    const id = ++toastId.current;
    setToasts(p => [...p, { id, msg, type }]);
    setTimeout(() => setToasts(p => p.filter(t => t.id !== id)), 3500);
  };

  // ── fetch user + stats on mount ──
  useEffect(() => {
    const fetchMe = async () => {
      try {
        const token = localStorage.getItem('veritas_token');
        if (!token) return;
        const res = await axios.get(`${API_URL}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
        setUser(res.data);
        setEditName(res.data.name || '');
        localStorage.setItem('veritas_user', JSON.stringify(res.data));
      } catch {}
    };
    const fetchStats = async () => {
      try {
        const token = localStorage.getItem('veritas_token');
        const res = await axios.get(`${API_URL}/api/history?limit=1000`, { headers: { Authorization: `Bearer ${token}` } });
        const scans = res.data.scans || [];
        const ai    = scans.filter(s => s.verdict === 'ai').length;
        const real  = scans.filter(s => s.verdict === 'real').length;
        setStats({ total: scans.length, ai, real, lastScan: scans[0]?.created_at || null });
      } catch {}
    };
    fetchMe();
    fetchStats();
    if (isPro) fetchApiKey();
  }, []);

  const fetchApiKey = async () => {
    try {
      const token = localStorage.getItem('veritas_token');
      const res = await axios.get(`${API_URL}/api/auth/api-key`, { headers: { Authorization: `Bearer ${token}` } });
      setApiKey(res.data.api_key);
    } catch {}
  };

  // ── save profile name ──
  const handleSaveProfile = async () => {
    if (!editName.trim()) return toast('El nombre no puede estar vacío', 'error');
    setSaving(true);
    try {
      const token = localStorage.getItem('veritas_token');
      const res = await axios.put(`${API_URL}/api/auth/profile`, { name: editName.trim() }, { headers: { Authorization: `Bearer ${token}` } });
      const updated = { ...user, name: res.data.name };
      setUser(updated);
      localStorage.setItem('veritas_user', JSON.stringify(updated));
      toast('Nombre actualizado correctamente');
    } catch (e) {
      toast(e.response?.data?.detail || 'Error al guardar', 'error');
    } finally { setSaving(false); }
  };

  // ── change password ──
  const handleChangePassword = async () => {
    if (!curPass || !newPass || !confPass) return toast('Rellena todos los campos', 'error');
    if (newPass !== confPass) return toast('Las contraseñas no coinciden', 'error');
    if (newPass.length < 6) return toast('Mínimo 6 caracteres', 'error');
    setSavingPass(true);
    try {
      const token = localStorage.getItem('veritas_token');
      await axios.put(`${API_URL}/api/auth/profile`, { current_password: curPass, new_password: newPass }, { headers: { Authorization: `Bearer ${token}` } });
      setCurPass(''); setNewPass(''); setConfPass('');
      toast('Contraseña actualizada correctamente');
    } catch (e) {
      toast(e.response?.data?.detail || 'Error al cambiar contraseña', 'error');
    } finally { setSavingPass(false); }
  };

  // ── generate api key ──
  const generateApiKey = async () => {
    setLoadingKey(true);
    try {
      const token = localStorage.getItem('veritas_token');
      const res = await axios.post(`${API_URL}/api/auth/generate-api-key`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setApiKey(res.data.api_key);
      toast('API Key generada');
    } catch { toast('Error al generar API key', 'error'); }
    finally { setLoadingKey(false); }
  };

  const copyApiKey = () => {
    if (!apiKey) return;
    navigator.clipboard.writeText(apiKey);
    setCopiedKey(true);
    setTimeout(() => setCopiedKey(false), 2000);
    toast('API Key copiada');
  };

  // ── stripe portal ──
  const handlePortal = async () => {
    setLoadingPortal(true);
    try {
      const token = localStorage.getItem('veritas_token');
      const res = await axios.post(`${API_URL}/api/stripe/portal`, {}, { headers: { Authorization: `Bearer ${token}` } });
      window.location.href = res.data.url;
    } catch (e) { toast(e.response?.data?.detail || 'Error al abrir portal', 'error'); }
    finally { setLoadingPortal(false); }
  };

  const handleUpgradeClick = async (plan = 'pro', billing = 'monthly') => {
    setLoadingCheckout(true);
    try {
      const token = localStorage.getItem('veritas_token');
      const res = await axios.post(`${API_URL}/api/stripe/create-checkout`, { plan, billing }, { headers: { Authorization: `Bearer ${token}` } });
      window.location.href = res.data.url;
    } catch (e) { toast(e.response?.data?.detail || 'Error al crear pago', 'error'); }
    finally { setLoadingCheckout(false); }
  };

  const handleSyncPlan = async () => {
    setSyncingPlan(true);
    try {
      const token = localStorage.getItem('veritas_token');
      const res = await axios.post(`${API_URL}/api/stripe/sync-plan`, {}, { headers: { Authorization: `Bearer ${token}` } });
      const upd = { ...user, plan: res.data.plan };
      setUser(upd);
      localStorage.setItem('veritas_user', JSON.stringify(upd));
      toast(`Plan sincronizado: ${res.data.plan}`);
    } catch (e) { toast(e.response?.data?.detail || 'Error al sincronizar', 'error'); }
    finally { setSyncingPlan(false); }
  };

  // ── batch ──
  const handleBatch = async () => {
    if (!batchFiles.length) return;
    setBatchLoading(true); setBatchError(null); setBatchResults([]);
    try {
      const token = localStorage.getItem('veritas_token');
      const fd = new FormData();
      batchFiles.forEach(f => fd.append('files', f));
      const res = await axios.post(`${API_URL}/api/analyze/batch`, fd, {
        headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` }, timeout: 120000
      });
      const results = [];
      for (const jobId of res.data.job_ids) {
        for (let i = 0; i < 60; i++) {
          const j = await axios.get(`${API_URL}/api/job/${jobId}`);
          if (j.data.status === 'completed') { results.push(j.data.result); break; }
          if (j.data.status === 'failed')    { results.push({ error: j.data.error }); break; }
          await new Promise(r => setTimeout(r, 2000));
        }
      }
      setBatchResults(results);
      setBatchFiles([]);
      toast(`${results.length} archivos analizados`);
    } catch (e) { setBatchError(e.response?.data?.detail || 'Error en el análisis'); }
    finally { setBatchLoading(false); }
  };

  // ── delete account ──
  const handleDeleteAccount = async () => {
    if (deleteConfirm !== user?.email) return toast('El email no coincide', 'error');
    setDeletingAcc(true);
    try {
      const token = localStorage.getItem('veritas_token');
      await axios.delete(`${API_URL}/api/auth/account`, { headers: { Authorization: `Bearer ${token}` } });
      localStorage.clear();
      onLogout();
    } catch (e) { toast(e.response?.data?.detail || 'Error al eliminar cuenta', 'error'); }
    finally { setDeletingAcc(false); }
  };

  // ── email verification ──
  const [resendingVerif, setResendingVerif] = useState(false);
  const [resentVerif, setResentVerif] = useState(false);

  const handleResendVerification = async () => {
    setResendingVerif(true);
    try {
      const token = localStorage.getItem('veritas_token');
      await axios.post(`${API_URL}/api/auth/resend-verification`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setResentVerif(true);
      toast('Email de verificación reenviado. Revisa tu bandeja de entrada.');
      setTimeout(() => setResentVerif(false), 10000);
    } catch (e) {
      toast(e.response?.data?.detail || 'Error al reenviar', 'error');
    } finally { setResendingVerif(false); }
  };

  // ── plan badge ──
  const PlanBadge = () => (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-mono font-bold uppercase tracking-wider border ${
      isBusiness ? 'bg-purple-500/15 text-purple-300 border-purple-500/30' :
      isPro       ? 'bg-yellow-500/15 text-yellow-300 border-yellow-500/30' :
                    'bg-zinc-800 text-zinc-400 border-zinc-700'
    }`}>
      {isBusiness ? <><Building2 className="w-3 h-3" /> Business</> : isPro ? <><Star className="w-3 h-3" /> Pro</> : <><Zap className="w-3 h-3" /> Free</>}
    </span>
  );

  // ── avatar initials ──
  const initials = (user?.name || 'U').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

  // ── tabs ──
  const TABS = [
    { id: 'overview',  label: 'Resumen',      icon: BarChart2 },
    { id: 'profile',   label: 'Perfil',       icon: User },
    { id: 'security',  label: 'Seguridad',    icon: Shield },
    { id: 'plan',      label: 'Plan',         icon: Package },
    { id: 'api',       label: 'API',          icon: Code },
    ...(isBusiness ? [{ id: 'team', label: 'Equipo', icon: Users }] : []),
    { id: 'danger',    label: 'Cuenta',       icon: Settings },
  ];

  // ── load email prefs ──
  const fetchEmailPrefs = async () => {
    setLoadingEmailPrefs(true);
    try {
      const token = localStorage.getItem('veritas_token');
      const res = await axios.get(`${API_URL}/api/auth/email-prefs`, { headers: { Authorization: `Bearer ${token}` } });
      setEmailPrefs(res.data);
    } catch {}
    finally { setLoadingEmailPrefs(false); }
  };

  const handleSaveEmailPrefs = async () => {
    setSavingEmailPrefs(true);
    try {
      const token = localStorage.getItem('veritas_token');
      await axios.put(`${API_URL}/api/auth/email-prefs`, emailPrefs, { headers: { Authorization: `Bearer ${token}` } });
      toast('Preferencias de email guardadas');
    } catch { toast('Error al guardar preferencias', 'error'); }
    finally { setSavingEmailPrefs(false); }
  };

  // ── leave team ──
  const handleLeaveTeam = async () => {
    setLeavingTeam(true);
    try {
      const token = localStorage.getItem('veritas_token');
      await axios.post(`${API_URL}/api/team/leave`, {}, { headers: { Authorization: `Bearer ${token}` } });
      const updated = { ...user, plan: 'free', team_owner_id: undefined };
      setUser(updated);
      localStorage.setItem('veritas_user', JSON.stringify(updated));
      setConfirmLeave(false);
      toast('Has salido del equipo. Tu plan ha vuelto a Free.');
    } catch (e) { toast(e.response?.data?.detail || 'Error al salir del equipo', 'error'); }
    finally { setLeavingTeam(false); }
  };

  // ── fetch team members ──
  const fetchTeam = async () => {
    if (!isBusiness) return;
    setTeamLoading(true);
    try {
      const token = localStorage.getItem('veritas_token');
      const res = await axios.get(`${API_URL}/api/team/members`, { headers: { Authorization: `Bearer ${token}` } });
      setTeamMembers(res.data.members || []);
      setTeamSeatsUsed(res.data.seats_used || 0);
      setTeamSeatsTotal(res.data.seats_total || 5);
    } catch {}
    finally { setTeamLoading(false); }
  };

  useEffect(() => { if (isBusiness) fetchTeam(); }, [isBusiness]);
  useEffect(() => { fetchEmailPrefs(); }, []);

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return toast('Introduce un email', 'error');
    setInviting(true);
    try {
      const token = localStorage.getItem('veritas_token');
      await axios.post(`${API_URL}/api/team/invite`, { email: inviteEmail.trim() }, { headers: { Authorization: `Bearer ${token}` } });
      setInviteEmail('');
      toast(`Invitación enviada a ${inviteEmail.trim()}`);
      fetchTeam();
    } catch (e) { toast(e.response?.data?.detail || 'Error al invitar', 'error'); }
    finally { setInviting(false); }
  };

  const handleRemoveMember = async (email) => {
    setRemovingEmail(email);
    setConfirmRemoveEmail(null);
    try {
      const token = localStorage.getItem('veritas_token');
      await axios.delete(`${API_URL}/api/team/members/${encodeURIComponent(email)}`, { headers: { Authorization: `Bearer ${token}` } });
      toast('Miembro eliminado del equipo');
      fetchTeam();
    } catch (e) { toast(e.response?.data?.detail || 'Error al eliminar', 'error'); }
    finally { setRemovingEmail(null); }
  };

  return (
    <div className="min-h-screen bg-zinc-950">
      {/* Background */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] rounded-full opacity-[0.06]"
          style={{ background: 'radial-gradient(ellipse, #3b82f6, transparent 70%)' }} />
        <div className="scanlines opacity-30" />
      </div>

      <Toast toasts={toasts} />

      {/* ── Header ── */}
      <header className="sticky top-0 z-40 border-b border-zinc-800/80 bg-zinc-950/95 backdrop-blur-xl">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-14 flex items-center gap-3 sm:gap-4">
          <button onClick={onBack} className="text-zinc-500 hover:text-white font-mono text-xs sm:text-sm transition-colors flex items-center gap-1.5 flex-shrink-0">
            ← <span className="hidden sm:inline">Volver</span>
          </button>
          <div className="flex items-center gap-2">
            <Scan className="w-4 h-4 text-blue-500" />
            <span className="font-heading text-sm font-black">VERITAS <span className="text-blue-500">SCAN</span></span>
          </div>
          <div className="ml-auto flex items-center gap-2 sm:gap-3">
            <PlanBadge />
            {user?.email?.toLowerCase() === 'erik.datum@gmail.com' && (
              <button onClick={() => window.location.href = '/admin'}
                className="flex items-center gap-1.5 text-red-400 hover:text-red-300 font-mono text-xs transition-colors px-2.5 sm:px-3 py-1.5 rounded-lg border border-red-500/30 hover:border-red-500/60 bg-red-500/5">
                <Shield className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Admin</span>
              </button>
            )}
            <button onClick={onLogout}
              className="flex items-center gap-1.5 text-zinc-500 hover:text-red-400 font-mono text-xs transition-colors px-2.5 sm:px-3 py-1.5 rounded-lg border border-zinc-800 hover:border-red-500/30">
              <LogOut className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Salir</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8">

        {/* ── Hero card ── */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-zinc-900 via-zinc-900 to-zinc-900/80 border border-zinc-800 rounded-2xl p-4 sm:p-6 mb-6 sm:mb-8">
          <div className="flex items-center gap-4">
            {/* Avatar */}
            <div className="relative flex-shrink-0">
              <div className="w-14 h-14 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center shadow-xl shadow-blue-900/30">
                <span className="font-heading text-xl sm:text-2xl font-black text-white">{initials}</span>
              </div>
              <div className={`absolute -bottom-1 -right-1 w-4 h-4 sm:w-5 sm:h-5 rounded-full border-2 border-zinc-900 ${isPro ? 'bg-yellow-400' : 'bg-zinc-600'}`} />
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <h1 className="font-heading text-lg sm:text-2xl font-black text-white truncate">{user?.name}</h1>
              <p className="text-zinc-500 font-mono text-xs sm:text-sm mt-0.5 truncate">{user?.email}</p>
              <div className="flex flex-wrap items-center gap-2 mt-2">
                <PlanBadge />
                {isGoogle && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-mono border bg-zinc-800 text-zinc-400 border-zinc-700 flex items-center gap-1">
                    <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg> Google
                  </span>
                )}
              </div>
            </div>

            {/* Quick stats — only on sm+ */}
            <div className="hidden sm:flex gap-4 sm:gap-6 flex-shrink-0">
              <div className="text-center">
                <p className="font-heading text-2xl font-black text-white">{stats?.total ?? '—'}</p>
                <p className="text-zinc-600 font-mono text-xs">Análisis</p>
              </div>
              <div className="text-center">
                <p className="font-heading text-2xl font-black text-red-400">{stats?.ai ?? '—'}</p>
                <p className="text-zinc-600 font-mono text-xs">IA det.</p>
              </div>
              <div className="text-center">
                <p className="font-heading text-2xl font-black text-emerald-400">{stats?.real ?? '—'}</p>
                <p className="text-zinc-600 font-mono text-xs">Reales</p>
              </div>
            </div>
          </div>

          {/* Stats row on mobile */}
          <div className="flex sm:hidden gap-4 mt-4 pt-4 border-t border-zinc-800">
            <div className="text-center flex-1">
              <p className="font-heading text-xl font-black text-white">{stats?.total ?? '—'}</p>
              <p className="text-zinc-600 font-mono text-xs">Análisis</p>
            </div>
            <div className="text-center flex-1">
              <p className="font-heading text-xl font-black text-red-400">{stats?.ai ?? '—'}</p>
              <p className="text-zinc-600 font-mono text-xs">IA det.</p>
            </div>
            <div className="text-center flex-1">
              <p className="font-heading text-xl font-black text-emerald-400">{stats?.real ?? '—'}</p>
              <p className="text-zinc-600 font-mono text-xs">Reales</p>
            </div>
          </div>
        </motion.div>

        {/* ── Email verification banner (solo para usuarios email no verificados) ── */}
        {user && !user.email_verified && user.auth_provider !== 'google' && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col sm:flex-row items-start sm:items-center gap-3 bg-amber-500/8 border border-amber-500/30 rounded-xl p-4 mb-6 sm:mb-8"
          >
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <div className="w-8 h-8 rounded-lg bg-amber-500/15 border border-amber-500/25 flex items-center justify-center flex-shrink-0 mt-0.5">
                <Mail className="w-4 h-4 text-amber-400" />
              </div>
              <div className="min-w-0">
                <p className="text-amber-300 font-mono text-sm font-bold">Email sin verificar</p>
                <p className="text-zinc-500 font-mono text-xs mt-0.5 leading-relaxed">
                  Verifica tu cuenta para mayor seguridad. Hemos enviado un enlace a <span className="text-zinc-400">{user.email}</span>.
                </p>
              </div>
            </div>
            <button
              onClick={handleResendVerification}
              disabled={resendingVerif || resentVerif}
              className={`flex items-center gap-2 px-4 py-2 font-mono text-xs font-bold uppercase tracking-wider rounded-xl transition-all flex-shrink-0 ${
                resentVerif
                  ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 cursor-default'
                  : 'bg-amber-500/15 hover:bg-amber-500/25 text-amber-300 border border-amber-500/30'
              }`}
            >
              {resendingVerif ? (
                <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Enviando...</>
              ) : resentVerif ? (
                <><CheckCircle className="w-3.5 h-3.5" /> Enviado</>
              ) : (
                <><Mail className="w-3.5 h-3.5" /> Reenviar email</>
              )}
            </button>
          </motion.div>
        )}

        {/* ── Tab nav ── */}
        <div className="flex gap-1 bg-zinc-900 border border-zinc-800 rounded-xl p-1 mb-6 sm:mb-8 overflow-x-auto scrollbar-hide -mx-1 px-1">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button key={id} onClick={() => setActiveTab(id)}
              className={`flex items-center gap-1.5 px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg font-mono text-xs uppercase tracking-wider transition-all whitespace-nowrap flex-shrink-0 sm:flex-1 justify-center ${
                activeTab === id
                  ? 'bg-zinc-800 text-white border border-zinc-700'
                  : 'text-zinc-500 hover:text-zinc-300'
              }`}>
              <Icon className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="hidden xs:inline sm:inline">{label}</span>
            </button>
          ))}
        </div>

        {/* ════════════════ OVERVIEW ════════════════ */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Stats grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <StatCard label="Total análisis" value={stats?.total ?? 0} sub="histórico" color="blue" />
              <StatCard label="IA detectada" value={stats?.ai ?? 0} sub="contenido sintético" color="amber" />
              <StatCard label="Contenido real" value={stats?.real ?? 0} sub="verificado" color="emerald" />
              <StatCard
                label="Precisión IA"
                value={stats?.total ? `${Math.round((stats.ai / stats.total) * 100)}%` : '—'}
                sub="ratio detección"
                color="purple"
              />
            </div>

            {/* Usage bar */}
            <Section title="Uso de análisis hoy" icon={Zap} accent="blue" delay={0.05}>
              {isPro ? (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-yellow-500/10 border border-yellow-500/20 flex items-center justify-center flex-shrink-0">
                    <Crown className="w-5 h-5 text-yellow-400" />
                  </div>
                  <div>
                    <p className="text-white font-mono text-sm font-bold">Análisis ilimitados activos</p>
                    <p className="text-zinc-500 font-mono text-xs mt-0.5">Sin límite diario con tu plan {isBusiness ? 'Business' : 'Pro'}</p>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex justify-between font-mono text-sm">
                    <span className="text-zinc-400">{usage?.used || 0} de {usage?.limit || 5} usados hoy</span>
                    <span className="text-zinc-600">{usage?.remaining || 5} restantes</span>
                  </div>
                  <div className="h-2.5 bg-zinc-800 rounded-full overflow-hidden">
                    <motion.div initial={{ width: 0 }}
                      animate={{ width: `${((usage?.used || 0) / (usage?.limit || 5)) * 100}%` }}
                      transition={{ duration: 0.8, ease: 'easeOut' }}
                      className={`h-full rounded-full ${(usage?.used || 0) >= (usage?.limit || 5) ? 'bg-red-500' : 'bg-blue-500'}`}
                    />
                  </div>
                  <p className="text-zinc-600 font-mono text-xs">Se reinicia cada día a medianoche (UTC)</p>
                </div>
              )}
            </Section>

            {/* Quick actions */}
            <Section title="Acciones rápidas" icon={ChevronRight} accent="zinc" delay={0.1}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  { label: 'Editar perfil', desc: 'Nombre y datos personales', tab: 'profile', icon: Edit3, color: 'text-blue-400' },
                  { label: 'Seguridad', desc: 'Contraseña y acceso', tab: 'security', icon: Shield, color: 'text-emerald-400' },
                  { label: 'Plan y facturación', desc: isPro ? 'Gestionar suscripción' : 'Mejorar plan', tab: 'plan', icon: Crown, color: 'text-yellow-400' },
                  { label: 'API Key', desc: isPro ? 'Tu clave de integración' : 'Disponible en Pro', tab: 'api', icon: Key, color: 'text-purple-400' },
                ].map(({ label, desc, tab, icon: Icon, color }) => (
                  <button key={tab} onClick={() => setActiveTab(tab)}
                    className="flex items-center gap-4 p-4 bg-zinc-800/50 hover:bg-zinc-800 border border-zinc-700/50 hover:border-zinc-600 rounded-xl transition-all text-left group">
                    <Icon className={`w-5 h-5 ${color} flex-shrink-0`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-mono text-sm font-bold">{label}</p>
                      <p className="text-zinc-500 font-mono text-xs mt-0.5">{desc}</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-zinc-600 group-hover:text-zinc-400 group-hover:translate-x-0.5 transition-all" />
                  </button>
                ))}
              </div>
            </Section>
          </div>
        )}

        {/* ════════════════ PROFILE ════════════════ */}
        {activeTab === 'profile' && (
          <div className="space-y-6">
            <Section title="Información personal" icon={User} accent="blue">
              <div className="space-y-5">
                {/* Avatar display */}
                <div className="flex items-center gap-4 pb-5 border-b border-zinc-800">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center shadow-lg">
                    <span className="font-heading text-xl font-black text-white">{initials}</span>
                  </div>
                  <div>
                    <p className="text-white font-mono text-sm font-bold">{user?.name}</p>
                    <p className="text-zinc-500 font-mono text-xs">{user?.email}</p>
                    <p className="text-zinc-600 font-mono text-xs mt-1 flex items-center gap-1">
                    {isGoogle
                        ? <><svg className="w-3 h-3" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg> Cuenta de Google</>
                      : <><User className="w-3 h-3" /> Cuenta con email</>}
                  </p>
                  </div>
                </div>

                <Field
                  label="Nombre completo"
                  value={editName}
                  onChange={setEditName}
                  placeholder="Tu nombre"
                  hint="Aparece en tu perfil y en los reportes"
                />

                <Field
                  label="Email"
                  value={user?.email || ''}
                  onChange={() => {}}
                  disabled
                  hint={isGoogle ? "El email de tu cuenta Google no se puede cambiar aquí" : "Contacta con soporte para cambiar el email"}
                />

                <div className="flex justify-end pt-2">
                  <button onClick={handleSaveProfile} disabled={savingProfile || editName === user?.name}
                    className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-500 disabled:bg-zinc-800 disabled:text-zinc-600 text-white font-mono text-sm rounded-xl transition-all">
                    {savingProfile ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Guardar cambios
                  </button>
                </div>
              </div>
            </Section>

            <Section title="Información de la cuenta" icon={Info} accent="zinc">
              <div className="space-y-3">
                {[
                  { label: 'ID de usuario', value: user?._id || user?.id || '—' },
                  { label: 'Plan actual', value: isBusiness ? 'Business' : isPro ? 'Pro' : 'Free' },
                  { label: 'Proveedor de autenticación', value: isGoogle ? 'Google OAuth' : 'Email + Contraseña' },
                ].map(({ label, value }) => (
                  <div key={label} className="flex items-center justify-between py-2.5 border-b border-zinc-800 last:border-0">
                    <span className="text-zinc-500 font-mono text-xs uppercase tracking-wider">{label}</span>
                    <span className="text-zinc-300 font-mono text-xs">{value}</span>
                  </div>
                ))}
              </div>
            </Section>
          </div>
        )}

        {/* ════════════════ SECURITY ════════════════ */}
        {activeTab === 'security' && (
          <div className="space-y-6">
            {isGoogle ? (
              <Section title="Seguridad" icon={Shield} accent="emerald">
                <div className="flex items-start gap-4 p-4 bg-blue-500/5 border border-blue-500/20 rounded-xl">
                  <div className="w-10 h-10 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-blue-400 font-bold text-lg">G</span>
                  </div>
                  <div>
                    <p className="text-white font-mono text-sm font-bold">Cuenta gestionada por Google</p>
                    <p className="text-zinc-400 font-mono text-xs mt-1 leading-relaxed">
                      Tu acceso se gestiona a través de Google OAuth. Para cambiar tu contraseña o activar la verificación en dos pasos, visita tu cuenta de Google.
                    </p>
                    <a href="https://myaccount.google.com/security" target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 mt-3 text-blue-400 hover:text-blue-300 font-mono text-xs transition-colors">
                      <ExternalLink className="w-3.5 h-3.5" /> Gestionar seguridad en Google
                    </a>
                  </div>
                </div>
              </Section>
            ) : (
              <>
                <Section title="Cambiar contraseña" icon={Lock} accent="emerald">
                  <div className="space-y-4">
                    <Field label="Contraseña actual" value={curPass} onChange={setCurPass} type="password" placeholder="Tu contraseña actual" />
                    <Field label="Nueva contraseña" value={newPass} onChange={setNewPass} type="password" placeholder="Mínimo 6 caracteres" hint="Usa letras, números y símbolos para mayor seguridad" />
                    <Field label="Confirmar nueva contraseña" value={confPass} onChange={setConfPass} type="password" placeholder="Repite la nueva contraseña" />

                    {/* Password strength */}
                    {newPass && (
                      <div className="space-y-1.5">
                        <p className="font-mono text-xs text-zinc-500 uppercase tracking-wider">Fortaleza</p>
                        <div className="flex gap-1.5">
                          {[1,2,3,4].map(i => {
                            const score = [newPass.length >= 6, /[A-Z]/.test(newPass), /[0-9]/.test(newPass), /[^a-zA-Z0-9]/.test(newPass)].filter(Boolean).length;
                            return <div key={i} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= score ? score <= 1 ? 'bg-red-500' : score <= 2 ? 'bg-amber-500' : score <= 3 ? 'bg-yellow-400' : 'bg-emerald-500' : 'bg-zinc-700'}`} />;
                          })}
                        </div>
                      </div>
                    )}

                    <div className="flex justify-end pt-1">
                      <button onClick={handleChangePassword} disabled={savingPass || !curPass || !newPass || !confPass}
                        className="flex items-center gap-2 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-500 disabled:bg-zinc-800 disabled:text-zinc-600 text-white font-mono text-sm rounded-xl transition-all">
                        {savingPass ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
                        Cambiar contraseña
                      </button>
                    </div>
                  </div>
                </Section>

<SessionInfo />
              </>
            )}
          </div>
        )}

        {/* ════════════════ PLAN ════════════════ */}
        {activeTab === 'plan' && (
          <div className="space-y-6">
            {isPro ? (
              <>
                <Section title={isBusiness ? 'Plan Business activo' : 'Plan Pro activo'} icon={Crown} accent={isBusiness ? 'purple' : 'yellow'}>
                  <div className="space-y-5">
                    {/* Active plan hero */}
                    <div className={`p-5 rounded-xl border ${isBusiness ? 'bg-purple-500/5 border-purple-500/25' : 'bg-yellow-500/5 border-yellow-500/25'}`}>
                      <div className="flex items-center gap-3 mb-3">
                        <Crown className={`w-6 h-6 ${isBusiness ? 'text-purple-400' : 'text-yellow-400'}`} />
                        <span className={`font-heading text-lg font-black ${isBusiness ? 'text-purple-300' : 'text-yellow-300'}`}>
                          {isBusiness ? 'Business' : 'Pro'}
                        </span>
                        <span className={`ml-auto text-xs font-mono px-2.5 py-1 rounded-full border ${isBusiness ? 'bg-purple-500/20 text-purple-400 border-purple-500/30' : 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'}`}>
                          Activo
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {(isBusiness ? [
                          'Análisis ilimitados', 'Lotes hasta 50 archivos',
                          'API 5.000 llamadas/día', 'Historial en la nube',
                          'Exportar PDF', 'Soporte prioritario',
                        ] : [
                          'Análisis ilimitados', 'Lotes hasta 10 archivos',
                          'API 500 llamadas/día', 'Historial en la nube',
                          'Exportar PDF', 'Sin anuncios',
                        ]).map(f => (
                          <div key={f} className="flex items-center gap-2 text-xs font-mono text-zinc-300">
                            <CheckCircle className={`w-3.5 h-3.5 flex-shrink-0 ${isBusiness ? 'text-purple-400' : 'text-yellow-400'}`} />
                            {f}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Renewal */}
                    {user?.subscription_end ? (() => {
                      const end  = new Date(user.subscription_end);
                      const days = Math.ceil((end - new Date()) / (1000 * 60 * 60 * 24));
                      const date = end.toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' });
                      return (
                        <div className="flex items-center justify-between bg-zinc-800 rounded-xl p-4 border border-zinc-700">
                          <div className="flex items-center gap-3">
                            <Calendar className="w-4 h-4 text-zinc-400" />
                            <div>
                              <p className="text-zinc-400 font-mono text-xs uppercase tracking-wider">Próxima renovación</p>
                              <p className="text-white font-mono text-sm mt-0.5">{date}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className={`font-heading text-3xl font-black ${days <= 7 ? 'text-red-400' : 'text-white'}`}>{days}</p>
                            <p className="text-zinc-500 font-mono text-xs">días</p>
                          </div>
                        </div>
                      );
                    })() : (
                      <div className="flex items-center gap-3 bg-zinc-800 rounded-xl p-4 border border-zinc-700">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isBusiness ? 'bg-purple-500/10 border border-purple-500/20' : 'bg-yellow-500/10 border border-yellow-500/20'}`}>
                          <CheckCircle className={`w-4 h-4 ${isBusiness ? 'text-purple-400' : 'text-yellow-400'}`} />
                        </div>
                        <div>
                          <p className="text-zinc-400 font-mono text-xs uppercase tracking-wider">Acceso</p>
                          <p className="text-white font-mono text-sm mt-0.5">Acceso permanente</p>
                        </div>
                      </div>
                    )}

                    <div className="flex gap-3">
                      <button onClick={handlePortal} disabled={loadingPortal}
                        className="flex-1 flex items-center justify-center gap-2 py-3 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 font-mono text-sm rounded-xl transition-all border border-zinc-700">
                        {loadingPortal ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                        Gestionar suscripción
                      </button>
                      <button onClick={handleSyncPlan} disabled={syncingPlan}
                        className="flex items-center gap-2 px-4 py-3 border border-zinc-700 hover:border-zinc-500 text-zinc-500 hover:text-zinc-300 font-mono text-sm rounded-xl transition-all"
                        title="Sincronizar plan con Stripe">
                        <RefreshCw className={`w-4 h-4 ${syncingPlan ? 'animate-spin' : ''}`} />
                      </button>
                    </div>
                  </div>
                </Section>
              </>
            ) : (
              <Section title="Mejora tu plan" icon={Package} accent="yellow">
                <div className="space-y-4">
                  <p className="text-zinc-400 font-mono text-sm">Actualmente en el plan <strong className="text-white">Free</strong> — 5 análisis/día por dispositivo.</p>

                  <div className="grid sm:grid-cols-2 gap-4">
                    {/* Pro */}
                    <div className="relative bg-zinc-800/60 border-2 border-yellow-500/40 rounded-2xl p-5 space-y-4">
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-yellow-500 text-black font-mono font-black text-xs px-3 py-0.5 rounded-full">Más popular</div>
                      <div className="flex items-center gap-2">
                        <Star className="w-5 h-5 text-yellow-400" />
                        <span className="font-heading text-lg font-black text-white">Pro</span>
                      </div>
                      <div>
                        <span className="font-heading text-3xl font-black text-white">$4.99</span>
                        <span className="text-zinc-500 font-mono text-sm">/mes</span>
                      </div>
                      <ul className="space-y-2">
                        {['Análisis ilimitados', 'Lotes 10 archivos', 'API 500/día', 'Historial nube', 'Exportar PDF'].map(f => (
                          <li key={f} className="flex items-center gap-2 text-zinc-300 font-mono text-xs">
                            <Check className="w-3.5 h-3.5 text-yellow-400 flex-shrink-0" />{f}
                          </li>
                        ))}
                      </ul>
                      <button onClick={() => handleUpgradeClick('pro', 'monthly')} disabled={loadingCheckout}
                        className="w-full py-2.5 bg-yellow-500 hover:bg-yellow-400 disabled:opacity-50 text-black font-mono font-bold text-sm rounded-xl transition-all flex items-center justify-center gap-2">
                        {loadingCheckout ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Empezar con Pro <ArrowRight className="w-4 h-4" /></>}
                      </button>
                    </div>

                    {/* Business */}
                    <div className="bg-zinc-800/60 border-2 border-purple-500/40 rounded-2xl p-5 space-y-4">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-5 h-5 text-purple-400" />
                        <span className="font-heading text-lg font-black text-white">Business</span>
                      </div>
                      <div>
                        <span className="font-heading text-3xl font-black text-white">$29</span>
                        <span className="text-zinc-500 font-mono text-sm">/mes</span>
                      </div>
                      <ul className="space-y-2">
                        {['Todo lo de Pro', 'Lotes 50 archivos', 'API 5.000/día', '5 usuarios', 'Soporte VIP'].map(f => (
                          <li key={f} className="flex items-center gap-2 text-zinc-300 font-mono text-xs">
                            <Check className="w-3.5 h-3.5 text-purple-400 flex-shrink-0" />{f}
                          </li>
                        ))}
                      </ul>
                      <button onClick={() => handleUpgradeClick('business', 'monthly')} disabled={loadingCheckout}
                        className="w-full py-2.5 bg-purple-600 hover:bg-purple-500 disabled:opacity-50 text-white font-mono font-bold text-sm rounded-xl transition-all flex items-center justify-center gap-2">
                        {loadingCheckout ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Empezar con Business <ArrowRight className="w-4 h-4" /></>}
                      </button>
                    </div>
                  </div>
                  <p className="text-zinc-600 font-mono text-xs text-center">Cancela cuando quieras · Pago seguro con Stripe</p>
                </div>
              </Section>
            )}

            {/* Batch analysis */}
            {isPro && (
              <Section title="Análisis por lotes" icon={Layers} accent="blue">
                <div className="space-y-4">
                  <p className="text-zinc-500 font-mono text-xs">Sube hasta <strong className="text-white">{isBusiness ? '50' : '10'}</strong> archivos a la vez para analizarlos en paralelo.</p>
                  <input ref={batchRef} type="file" multiple
                    accept="image/jpeg,image/png,image/gif,image/webp,application/pdf,text/plain,.docx"
                    className="hidden" onChange={e => setBatchFiles(Array.from(e.target.files))} />
                  <div className="flex gap-2">
                    <button onClick={() => batchRef.current?.click()}
                      className="flex-1 flex items-center justify-center gap-2 py-2.5 border border-zinc-700 hover:border-zinc-500 text-zinc-400 hover:text-white font-mono text-xs rounded-xl transition-all">
                      <Upload className="w-4 h-4" />
                      {batchFiles.length ? `${batchFiles.length} archivo(s) listos` : 'Seleccionar archivos'}
                    </button>
                    <button onClick={handleBatch} disabled={!batchFiles.length || batchLoading}
                      className="px-5 py-2.5 bg-blue-600 hover:bg-blue-500 disabled:bg-zinc-800 disabled:text-zinc-600 text-white font-mono text-xs rounded-xl transition-all flex items-center gap-2">
                      {batchLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Analizar'}
                    </button>
                  </div>
                  {batchError && (
                    <p className="text-red-400 font-mono text-xs flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />{batchError}
                    </p>
                  )}
                  {batchResults.length > 0 && (
                    <div className="space-y-1.5 max-h-56 overflow-y-auto">
                      {batchResults.map((r, i) => r && !r.error ? (
                        <div key={i} className={`flex items-center gap-3 px-3 py-2 rounded-lg border text-xs font-mono ${
                          r.verdict === 'real' ? 'border-emerald-500/25 bg-emerald-500/5 text-emerald-300' : 'border-red-500/25 bg-red-500/5 text-red-300'
                        }`}>
                          <FileImage className="w-3.5 h-3.5 text-zinc-500 flex-shrink-0" />
                          <span className="flex-1 truncate text-zinc-300">{r.file_name}</span>
                          <span className="font-bold">{r.verdict === 'real' ? 'REAL' : 'IA'}</span>
                          <span className="text-zinc-500">{r.confidence}%</span>
                        </div>
                      ) : (
                        <div key={i} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-red-500/25 text-red-400 text-xs font-mono">
                          <X className="w-3 h-3" /> Error en archivo {i + 1}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Section>
            )}
          </div>
        )}

        {/* ════════════════ API ════════════════ */}
        {activeTab === 'api' && (
          <div className="space-y-6">
            {isPro ? (
              <>
                <Section title="Tu API Key" icon={Key} accent={isBusiness ? 'purple' : 'blue'}>
                  <div className="space-y-4">
                    <p className="text-zinc-400 font-mono text-sm">
                      Clave personal para integrar Veritas Scan en tus apps. Límite: <strong className="text-white">{isBusiness ? '5.000' : '500'}</strong> llamadas/día.
                    </p>

                    {apiKey ? (
                      <div className="space-y-3">
                        <div className="flex items-center gap-2 bg-zinc-800 border border-zinc-700 rounded-xl px-4 py-3">
                          <Key className="w-4 h-4 text-zinc-500 flex-shrink-0" />
                          <code className="flex-1 text-blue-300 font-mono text-sm truncate">
                            {showKey ? apiKey : `${apiKey.slice(0, 8)}${'•'.repeat(24)}${apiKey.slice(-4)}`}
                          </code>
                          <button onClick={() => setShowKey(s => !s)} className="text-zinc-500 hover:text-white transition-colors flex-shrink-0">
                            {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                          <button onClick={copyApiKey} className="text-zinc-500 hover:text-white transition-colors flex-shrink-0">
                            {copiedKey ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                          </button>
                        </div>
                        <div className="flex gap-2">
                          <button onClick={generateApiKey} disabled={loadingApiKey}
                            className="flex items-center gap-2 text-zinc-500 hover:text-zinc-300 font-mono text-xs transition-colors border border-zinc-700 hover:border-zinc-500 px-3 py-2 rounded-lg">
                            <RefreshCw className={`w-3.5 h-3.5 ${loadingApiKey ? 'animate-spin' : ''}`} />
                            Regenerar key
                          </button>
                          {onNavigateApiDocs && (
                            <button onClick={onNavigateApiDocs}
                              className="flex items-center gap-2 text-zinc-500 hover:text-zinc-300 font-mono text-xs transition-colors border border-zinc-700 hover:border-zinc-500 px-3 py-2 rounded-lg">
                              <ExternalLink className="w-3.5 h-3.5" />
                              Ver docs
                            </button>
                          )}
                        </div>
                      </div>
                    ) : (
                      <button onClick={generateApiKey} disabled={loadingApiKey}
                        className="flex items-center gap-2 px-5 py-3 bg-blue-600 hover:bg-blue-500 disabled:bg-zinc-800 text-white font-mono text-sm rounded-xl transition-all">
                        {loadingApiKey ? <Loader2 className="w-4 h-4 animate-spin" /> : <Key className="w-4 h-4" />}
                        Generar API Key
                      </button>
                    )}
                  </div>
                </Section>

                <Section title="Ejemplo de uso" icon={Code} accent="zinc">
                  <div className="space-y-3">
                    <div className="bg-zinc-800 border border-zinc-700 rounded-xl overflow-hidden">
                      <div className="flex items-center justify-between px-4 py-2 border-b border-zinc-700 bg-zinc-800/80">
                        <span className="font-mono text-xs text-zinc-500 uppercase tracking-wider">cURL</span>
                      </div>
                      <pre className="px-4 py-4 text-zinc-300 font-mono text-xs leading-relaxed overflow-x-auto">
{`curl -X POST \\
  https://veritas-scan-production.up.railway.app/api/v1/analyze \\
  -H "X-API-Key: ${apiKey || 'vs_tu_api_key'}" \\
  -F "file=@imagen.jpg"`}
                      </pre>
                    </div>
                    <div className="bg-zinc-800 border border-zinc-700 rounded-xl overflow-hidden">
                      <div className="flex items-center justify-between px-4 py-2 border-b border-zinc-700 bg-zinc-800/80">
                        <span className="font-mono text-xs text-zinc-500 uppercase tracking-wider">Respuesta</span>
                      </div>
                      <pre className="px-4 py-4 text-zinc-300 font-mono text-xs leading-relaxed overflow-x-auto">
{`{
  "job_id": "uuid-del-job",
  "status": "pending"
}`}
                      </pre>
                    </div>
                    {onNavigateApiDocs && (
                      <button onClick={onNavigateApiDocs}
                        className="w-full flex items-center justify-center gap-2 py-3 border border-zinc-700 hover:border-blue-500 text-zinc-400 hover:text-blue-400 font-mono text-sm rounded-xl transition-all">
                        <ExternalLink className="w-4 h-4" /> Documentación completa de la API
                      </button>
                    )}
                  </div>
                </Section>
              </>
            ) : (
              <Section title="API pública" icon={Key} accent="zinc">
                <div className="space-y-5">
                  <div className="p-5 bg-zinc-800/50 border border-zinc-700/50 rounded-xl">
                    <Lock className="w-8 h-8 text-zinc-600 mb-3" />
                    <p className="text-white font-mono text-sm font-bold mb-1">Disponible en plan Pro o Business</p>
                    <p className="text-zinc-500 font-mono text-xs leading-relaxed">
                      Integra Veritas Scan directamente en tus apps, scripts y flujos de trabajo mediante HTTP.
                    </p>
                  </div>
                  <ul className="space-y-2.5">
                    {[
                      { icon: '⚡', text: '500 llamadas/día en Pro · 5.000 en Business' },
                      { icon: '🔁', text: 'Integra en scripts, pipelines o dashboards' },
                      { icon: '🖼️', text: 'Soporta imágenes, PDFs, texto y URLs' },
                      { icon: '🔑', text: 'API Key generada desde tu perfil' },
                    ].map(({ icon, text }) => (
                      <li key={text} className="flex items-center gap-3 text-zinc-400 font-mono text-xs">
                        <span className="text-base">{icon}</span>{text}
                      </li>
                    ))}
                  </ul>
                  <button onClick={() => setActiveTab('plan')}
                    className="w-full flex items-center justify-center gap-2 py-3 bg-blue-600 hover:bg-blue-500 text-white font-mono text-sm rounded-xl transition-all">
                    Ver planes <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </Section>
            )}
          </div>
        )}

        {/* ════════════════ TEAM ════════════════ */}
        {activeTab === 'team' && (
          <div className="space-y-6">
            <Section title="Equipo Business" icon={Users} accent="purple">
              <div className="space-y-6">

                {/* Seats meter */}
                <div className="p-4 bg-purple-500/5 border border-purple-500/20 rounded-xl">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-mono text-xs text-zinc-400 uppercase tracking-wider">Asientos usados</span>
                    <span className="font-mono text-sm font-bold text-purple-300">{teamSeatsUsed + 1} / {teamSeatsTotal}</span>
                  </div>
                  <div className="w-full bg-zinc-800 rounded-full h-2">
                    <div
                      className="bg-purple-500 h-2 rounded-full transition-all"
                      style={{ width: `${((teamSeatsUsed + 1) / teamSeatsTotal) * 100}%` }}
                    />
                  </div>
                  <p className="text-zinc-600 font-mono text-xs mt-2">
                    Tú ocupas 1 asiento • Puedes invitar hasta {teamSeatsTotal - 1 - teamSeatsUsed} personas más
                  </p>
                </div>

                {/* Invite form */}
                {teamSeatsUsed < teamSeatsTotal - 1 ? (
                  <div className="space-y-3">
                    <label className="font-mono text-xs text-zinc-500 uppercase tracking-wider">Invitar nuevo miembro</label>
                    <div className="flex gap-2">
                      <input
                        value={inviteEmail}
                        onChange={e => setInviteEmail(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleInvite()}
                        placeholder="email@empresa.com"
                        className="flex-1 bg-zinc-800 border border-zinc-700 focus:border-purple-500 rounded-xl px-4 py-2.5 text-zinc-200 placeholder-zinc-600 font-mono text-sm outline-none transition-colors"
                      />
                      <button
                        onClick={handleInvite}
                        disabled={inviting || !inviteEmail.trim()}
                        className="flex items-center gap-2 px-4 py-2.5 bg-purple-600 hover:bg-purple-500 disabled:bg-zinc-800 disabled:text-zinc-600 text-white font-mono text-sm rounded-xl transition-all flex-shrink-0"
                      >
                        {inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                        <span className="hidden sm:inline">Invitar</span>
                      </button>
                    </div>
                    <p className="text-zinc-600 font-mono text-xs">El invitado recibirá un email con un enlace para unirse al equipo y activar el plan Business.</p>
                  </div>
                ) : (
                  <div className="p-4 bg-zinc-800/50 border border-zinc-700/50 rounded-xl">
                    <p className="text-zinc-400 font-mono text-sm font-bold mb-1">Asientos agotados</p>
                    <p className="text-zinc-600 font-mono text-xs">Has alcanzado el límite de {teamSeatsTotal} usuarios. Elimina un miembro para poder invitar a otro.</p>
                  </div>
                )}

                {/* Members list */}
                <div className="space-y-3">
                  <p className="font-mono text-xs text-zinc-500 uppercase tracking-wider">Miembros invitados</p>

                  {/* Owner row */}
                  <div className="flex items-center gap-3 p-3 bg-zinc-800/40 border border-zinc-700/40 rounded-xl">
                    <div className="w-8 h-8 rounded-lg bg-purple-600 flex items-center justify-center flex-shrink-0">
                      <span className="font-heading text-xs font-black text-white">{(user?.name || 'T')[0].toUpperCase()}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-mono text-sm font-bold truncate">{user?.email}</p>
                      <p className="text-zinc-500 font-mono text-xs">Tú (propietario)</p>
                    </div>
                    <span className="px-2 py-0.5 bg-purple-500/15 text-purple-400 border border-purple-500/25 rounded-full font-mono text-xs flex-shrink-0">Owner</span>
                  </div>

                  {teamLoading ? (
                    <div className="flex items-center gap-2 py-4 text-zinc-500 font-mono text-sm">
                      <Loader2 className="w-4 h-4 animate-spin" /> Cargando...
                    </div>
                  ) : teamMembers.length === 0 ? (
                    <div className="flex flex-col items-center gap-2 py-8 text-center">
                      <Mail className="w-8 h-8 text-zinc-700" />
                      <p className="text-zinc-500 font-mono text-sm">Aún no has invitado a nadie</p>
                      <p className="text-zinc-600 font-mono text-xs">Invita hasta {teamSeatsTotal - 1} personas a tu equipo</p>
                    </div>
                  ) : (
                    teamMembers.map(member => (
                      <div key={member.email} className="flex items-center gap-3 p-3 bg-zinc-800/40 border border-zinc-700/40 rounded-xl">
                        <div className="w-8 h-8 rounded-lg bg-zinc-700 flex items-center justify-center flex-shrink-0">
                          <span className="font-heading text-xs font-black text-zinc-300">{member.email[0].toUpperCase()}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-mono text-sm truncate">{member.email}</p>
                          <p className="text-zinc-500 font-mono text-xs">
                            {member.status === 'accepted' ? '✓ Aceptado' : '⏳ Pendiente de aceptar'}
                          </p>
                        </div>
                        <span className={`px-2 py-0.5 rounded-full font-mono text-xs border flex-shrink-0 ${
                          member.status === 'accepted'
                            ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/25'
                            : 'bg-amber-500/15 text-amber-400 border-amber-500/25'
                        }`}>
                          {member.status === 'accepted' ? 'Activo' : 'Invitado'}
                        </span>
                        <button
                          onClick={() => setConfirmRemoveEmail(member.email)}
                          disabled={removingEmail === member.email}
                          className="w-8 h-8 flex items-center justify-center text-zinc-600 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all flex-shrink-0 disabled:opacity-50"
                          title="Eliminar miembro"
                        >
                          {removingEmail === member.email
                            ? <Loader2 className="w-4 h-4 animate-spin" />
                            : <UserMinus className="w-4 h-4" />}
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </Section>
          </div>
        )}

        {/* ════════════════ CUENTA + DANGER ZONE ════════════════ */}
        {activeTab === 'danger' && (
          <div className="space-y-6">
            <Section title="Exportar datos" icon={FileText} accent="zinc">
              <div className="space-y-3">
                <p className="text-zinc-400 font-mono text-sm">Descarga toda la información asociada a tu cuenta.</p>
                <button
                  onClick={() => toast('Exportación disponible próximamente', 'warn')}
                  className="flex items-center gap-2 px-5 py-2.5 border border-zinc-700 hover:border-zinc-500 text-zinc-400 hover:text-white font-mono text-sm rounded-xl transition-all">
                  <FileText className="w-4 h-4" /> Exportar mis datos (JSON)
                </button>
              </div>
            </Section>

            <Section title="Cerrar sesión" icon={LogOut} accent="zinc">
              <div className="space-y-3">
                <p className="text-zinc-400 font-mono text-sm">Cierra tu sesión actual en este dispositivo.</p>
                <button onClick={onLogout}
                  className="flex items-center gap-2 px-5 py-2.5 border border-zinc-700 hover:border-zinc-500 text-zinc-400 hover:text-white font-mono text-sm rounded-xl transition-all">
                  <LogOut className="w-4 h-4" /> Cerrar sesión
                </button>
              </div>
            </Section>

            {/* Email preferences */}
            <Section title="Preferencias de email" icon={Bell} accent="zinc">
              <div className="space-y-5">
                <p className="text-zinc-400 font-mono text-sm">Controla qué emails recibes de Veritas Scan.</p>
                {loadingEmailPrefs ? (
                  <div className="flex items-center gap-2 text-zinc-500 font-mono text-sm">
                    <Loader2 className="w-4 h-4 animate-spin" /> Cargando...
                  </div>
                ) : (
                  <div className="space-y-3">
                    {/* Transactional - always on, can't disable */}
                    <div className="flex items-center justify-between p-4 bg-zinc-800/50 border border-zinc-700/50 rounded-xl">
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-mono text-sm font-bold">Emails de cuenta</p>
                        <p className="text-zinc-500 font-mono text-xs mt-0.5">Verificación, cambios de contraseña, facturas. No se pueden desactivar.</p>
                      </div>
                      <div className="w-10 h-6 bg-blue-600 rounded-full flex-shrink-0 ml-4 relative">
                        <div className="absolute right-1 top-1 w-4 h-4 bg-white rounded-full" />
                      </div>
                    </div>
                    {/* Marketing / novedades */}
                    <div className="flex items-center justify-between p-4 bg-zinc-800/50 border border-zinc-700/50 rounded-xl">
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-mono text-sm font-bold">Novedades y actualizaciones</p>
                        <p className="text-zinc-500 font-mono text-xs mt-0.5">Nuevas funciones, mejoras y anuncios del producto.</p>
                      </div>
                      <button
                        onClick={() => setEmailPrefs(p => ({ ...p, marketing: !p.marketing }))}
                        className={`w-10 h-6 rounded-full flex-shrink-0 ml-4 relative transition-colors ${
                          emailPrefs.marketing ? 'bg-blue-600' : 'bg-zinc-700'
                        }`}
                      >
                        <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${
                          emailPrefs.marketing ? 'right-1' : 'left-1'
                        }`} />
                      </button>
                    </div>
                    {/* Blog */}
                    <div className="flex items-center justify-between p-4 bg-zinc-800/50 border border-zinc-700/50 rounded-xl">
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-mono text-sm font-bold">Nuevos artículos del blog</p>
                        <p className="text-zinc-500 font-mono text-xs mt-0.5">Te avisamos cuando publicamos un nuevo post con su nombre y enlace.</p>
                      </div>
                      <button
                        onClick={() => setEmailPrefs(p => ({ ...p, blog: !p.blog }))}
                        className={`w-10 h-6 rounded-full flex-shrink-0 ml-4 relative transition-colors ${
                          emailPrefs.blog ? 'bg-blue-600' : 'bg-zinc-700'
                        }`}
                      >
                        <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${
                          emailPrefs.blog ? 'right-1' : 'left-1'
                        }`} />
                      </button>
                    </div>
                    <div className="flex justify-end">
                      <button
                        onClick={handleSaveEmailPrefs}
                        disabled={savingEmailPrefs}
                        className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-500 disabled:bg-zinc-800 disabled:text-zinc-600 text-white font-mono text-sm rounded-xl transition-all"
                      >
                        {savingEmailPrefs ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        Guardar preferencias
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </Section>

            {/* Leave team option for members (not owners) */}
            {user?.team_owner_id && (
              <Section title="Equipo" icon={Users} accent="purple">
                <div className="space-y-4">
                  <div className="p-4 bg-purple-500/5 border border-purple-500/20 rounded-xl">
                    <p className="text-purple-300 font-mono text-sm font-bold mb-1">Eres miembro de un equipo Business</p>
                    <p className="text-zinc-400 font-mono text-xs leading-relaxed">
                      Actualmente formas parte del equipo de otra persona. Si sales, tu plan volverá a ser <strong className="text-white">Free</strong>.
                    </p>
                  </div>
                  <button
                    onClick={() => setConfirmLeave(true)}
                    className="flex items-center gap-2 px-5 py-2.5 border border-purple-500/40 hover:border-purple-500 hover:bg-purple-500/10 text-purple-400 hover:text-purple-300 font-mono text-sm rounded-xl transition-all"
                  >
                    <UserMinus className="w-4 h-4" /> Salir del equipo
                  </button>
                </div>
              </Section>
            )}

            <Section title="Zona de peligro" icon={AlertTriangle} accent="red">
              <div className="space-y-4">
                <div className="p-4 bg-red-500/5 border border-red-500/20 rounded-xl">
                  <p className="text-red-400 font-mono text-sm font-bold mb-1">Eliminar cuenta permanentemente</p>
                  <p className="text-zinc-400 font-mono text-xs leading-relaxed">
                    Esta acción <strong className="text-red-400">no se puede deshacer</strong>. Se eliminarán tu cuenta, historial de análisis y todos tus datos. 
                    {isPro && ' Tu suscripción activa se cancelará automáticamente.'}
                  </p>
                </div>
                <button onClick={() => setDeleteOpen(true)}
                  className="flex items-center gap-2 px-5 py-2.5 border border-red-500/40 hover:border-red-500 hover:bg-red-500/10 text-red-400 hover:text-red-300 font-mono text-sm rounded-xl transition-all">
                  <Trash2 className="w-4 h-4" /> Eliminar mi cuenta
                </button>
              </div>
            </Section>
          </div>
        )}
      </div>

      {/* ── Confirm remove member modal ── */}
      <AnimatePresence>
        {confirmRemoveEmail && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/80 z-50 backdrop-blur-sm" onClick={() => setConfirmRemoveEmail(null)} />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: 'spring', stiffness: 400, damping: 30 }}
              className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 bg-zinc-900 border border-red-500/30 rounded-2xl w-[calc(100vw-2rem)] max-w-sm shadow-2xl overflow-hidden"
            >
              <div className="p-6 space-y-5">
                <div className="text-center space-y-3">
                  <div className="w-14 h-14 rounded-2xl bg-red-500/10 border border-red-500/20 flex items-center justify-center mx-auto">
                    <UserMinus className="w-7 h-7 text-red-400" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-black text-white">¿Eliminar miembro?</h3>
                    <p className="text-zinc-400 font-mono text-sm mt-1 break-all">{confirmRemoveEmail}</p>
                  </div>
                </div>
                <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-4">
                  <p className="text-amber-300 font-mono text-xs leading-relaxed">
                    Esta persona perderá el acceso Business inmediatamente y su plan volverá a <strong>Free</strong>.
                  </p>
                </div>
                <div className="flex gap-3">
                  <button onClick={() => setConfirmRemoveEmail(null)}
                    className="flex-1 py-2.5 border border-zinc-700 hover:border-zinc-500 text-zinc-400 hover:text-white font-mono text-sm rounded-xl transition-all">
                    Cancelar
                  </button>
                  <button
                    onClick={() => handleRemoveMember(confirmRemoveEmail)}
                    disabled={removingEmail === confirmRemoveEmail}
                    className="flex-1 py-2.5 bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white font-mono text-sm rounded-xl transition-all flex items-center justify-center gap-2">
                    {removingEmail === confirmRemoveEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserMinus className="w-4 h-4" />}
                    Sí, eliminar
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* ── Confirm leave team modal ── */}
      <AnimatePresence>
        {confirmLeave && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/80 z-50 backdrop-blur-sm" onClick={() => setConfirmLeave(false)} />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: 'spring', stiffness: 400, damping: 30 }}
              className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 bg-zinc-900 border border-purple-500/30 rounded-2xl w-[calc(100vw-2rem)] max-w-sm shadow-2xl overflow-hidden"
            >
              <div className="p-6 space-y-5">
                <div className="text-center space-y-3">
                  <div className="w-14 h-14 rounded-2xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center mx-auto">
                    <Users className="w-7 h-7 text-purple-400" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-black text-white">¿Salir del equipo?</h3>
                    <p className="text-zinc-400 font-mono text-sm mt-1">Esta acción no se puede deshacer</p>
                  </div>
                </div>
                <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-4 space-y-2">
                  {[
                    'Perderás el acceso al plan Business',
                    'Tu plan volverá a ser Free (5 análisis/día)',
                    'Podrás volver si el dueño te reinvita',
                  ].map(item => (
                    <div key={item} className="flex items-center gap-2 text-amber-300 font-mono text-xs">
                      <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" /> {item}
                    </div>
                  ))}
                </div>
                <div className="flex gap-3">
                  <button onClick={() => setConfirmLeave(false)}
                    className="flex-1 py-2.5 border border-zinc-700 hover:border-zinc-500 text-zinc-400 hover:text-white font-mono text-sm rounded-xl transition-all">
                    Cancelar
                  </button>
                  <button
                    onClick={handleLeaveTeam}
                    disabled={leavingTeam}
                    className="flex-1 py-2.5 bg-purple-600 hover:bg-purple-500 disabled:opacity-50 text-white font-mono text-sm rounded-xl transition-all flex items-center justify-center gap-2">
                    {leavingTeam ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserMinus className="w-4 h-4" />}
                    Sí, salir
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* ── Delete account modal ── */}
      <AnimatePresence>
        {deleteOpen && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/80 z-50 backdrop-blur-sm" onClick={() => setDeleteOpen(false)} />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: 'spring', stiffness: 400, damping: 30 }}
              className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 bg-zinc-900 border border-red-500/30 rounded-2xl w-[calc(100vw-2rem)] max-w-md shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6 space-y-5">
                {/* Icon + title */}
                <div className="text-center space-y-3">
                  <div className="w-14 h-14 rounded-2xl bg-red-500/10 border border-red-500/20 flex items-center justify-center mx-auto">
                    <Trash2 className="w-7 h-7 text-red-400" />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl font-black text-white">¿Eliminar cuenta?</h3>
                    <p className="text-zinc-400 font-mono text-sm mt-1">Esta acción es irreversible</p>
                  </div>
                </div>

                {/* Warning list */}
                <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-4 space-y-2">
                  {[
                    'Tu perfil y datos personales',
                    'Todo tu historial de análisis',
                    isPro && 'Tu suscripción activa se cancelará',
                    'Tu API key quedará invalidada',
                  ].filter(Boolean).map(item => (
                    <div key={item} className="flex items-center gap-2 text-red-300 font-mono text-xs">
                      <X className="w-3.5 h-3.5 flex-shrink-0" /> {item}
                    </div>
                  ))}
                </div>

                {/* Confirm email */}
                <div className="space-y-2">
                  <label className="font-mono text-xs text-zinc-500 uppercase tracking-wider">
                    Escribe tu email para confirmar
                  </label>
                  <input
                    value={deleteConfirm}
                    onChange={e => setDeleteConfirm(e.target.value)}
                    placeholder={user?.email}
                    className="w-full bg-zinc-800 border border-red-500/30 focus:border-red-500 rounded-xl px-4 py-2.5 text-zinc-200 placeholder-zinc-600 font-mono text-sm outline-none transition-colors"
                  />
                </div>

                {/* Actions */}
                <div className="flex gap-3">
                  <button onClick={() => { setDeleteOpen(false); setDeleteConfirm(''); }}
                    className="flex-1 py-2.5 border border-zinc-700 hover:border-zinc-500 text-zinc-400 hover:text-white font-mono text-sm rounded-xl transition-all">
                    Cancelar
                  </button>
                  <button onClick={handleDeleteAccount}
                    disabled={deletingAcc || deleteConfirm !== user?.email}
                    className="flex-1 py-2.5 bg-red-600 hover:bg-red-500 disabled:bg-zinc-800 disabled:text-zinc-600 text-white font-mono text-sm rounded-xl transition-all flex items-center justify-center gap-2">
                    {deletingAcc ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                    Eliminar todo
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
